import React, { useState, useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const RevenueExpenseChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const chartData = useMemo(() => {
        const getLast12Months = () => {
            const months = [];
            const now = new Date();
            for (let i = 11; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const monthLabel = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
                const yearLabel = d.getFullYear().toString().slice(-2);
                months.push({ matchKey: monthLabel, displayStr: `${monthLabel}/${yearLabel}` });
            }
            return months;
        };

        const last12Months = getLast12Months();

        return last12Months.map(item => {
            const found = data.find(d => d.month?.toUpperCase() === item.matchKey);
            return found
                ? { ...found, month: item.matchKey, display: item.displayStr }
                : { month: item.matchKey, display: item.displayStr, paidIncome: 0, paidExpense: 0, paidNet: 0 };
        });
    }, [data]);

    const maxVal = Math.max(10, ...chartData.map(d => Math.max(d.paidIncome || 0, d.paidExpense || 0))) * 1.2;

    const formatFullCurrency = (value) => {
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    // Cores outonais
    const COLORS = {
        income: "#e2661c",     // Laranja (Entradas)
        expense: "#482712",    // Marrom (Saídas)
        net: "#10b981",        // Verde (Líquido)
        prevLine: "#ebdcc5"    // Creme
    };

    // Gera pontos para curvas SVG
    const incomePoints = chartData.map((item, idx) => ({
        x: (100 / Math.max(1, chartData.length)) * (idx + 0.5),
        y: 100 - ((item.paidIncome || 0) / maxVal) * 100
    }));

    const expensePoints = chartData.map((item, idx) => ({
        x: (100 / Math.max(1, chartData.length)) * (idx + 0.5),
        y: 100 - ((item.paidExpense || 0) / maxVal) * 100
    }));

    const netPoints = chartData.map((item, idx) => ({
        x: (100 / Math.max(1, chartData.length)) * (idx + 0.5),
        y: Math.min(100 - (Math.max(item.paidNet || 0, 0) / maxVal) * 100, 100)
    }));

    // Catmull-Rom splines (mesmo padrão do GrowthChart)
    const buildSmoothPath = (pts) => {
        if (pts.length === 0) return "";
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        const tension = 0.3;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }

        return path;
    };

    const incomeSmoothPath = buildSmoothPath(incomePoints);
    const expenseSmoothPath = buildSmoothPath(expensePoints);
    const netSmoothPath = buildSmoothPath(netPoints);

    return (
        <Card 
            className="neo-stat-card h-100" 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="neo-chart-card-header">
                <div className="neo-chart-header-container">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{ 
                                width: "38px", 
                                height: "38px", 
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-chart-areaspline font-size-18"></i>
                        </div>
                        <h5 className="neo-chart-title">Fluxo de Caixa</h5>
                    </div>
                    <div className="neo-chart-legend-container">
                        <div className="d-flex align-items-center">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: COLORS.income, marginRight: "6px" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>Entradas</small>
                        </div>
                        <div className="d-flex align-items-center ms-2">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: COLORS.expense, border: "1px solid rgba(72, 39, 18, 0.15)", marginRight: "6px" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>Saídas</small>
                        </div>
                        <div className="d-flex align-items-center ms-2">
                            <span style={{ width: "12px", height: "3px", borderRadius: "2px", backgroundColor: COLORS.net, marginRight: "6px", display: "inline-block" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>Líquido</small>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 mt-2">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "260px", display: "flex", alignItems: "flex-end" }}>
                        <div style={{ height: "200px", position: "relative", paddingLeft: "10px", paddingRight: "10px", minWidth: "550px", width: "100%" }}>

                        {chartData.every(d => !d.paidIncome && !d.paidExpense) ? (
                            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                                <i className="mdi mdi-chart-box-outline font-size-24 mb-2 opacity-50" style={{ color: "#784b22" }} />
                                <span className="font-size-12 fw-bold opacity-75" style={{ color: "#784b22" }}>Nenhum dado financeiro disponível</span>
                            </div>
                        ) : (
                            <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "35px", gap: "10px" }}>
                                
                                {/* SVG das Curvas */}
                                <svg 
                                    style={{ 
                                        position: "absolute", 
                                        top: 0, 
                                        left: 0, 
                                        width: "100%", 
                                        height: "calc(100% - 35px)", 
                                        pointerEvents: "none",
                                        zIndex: 4 
                                    }}
                                    preserveAspectRatio="none"
                                    viewBox="0 0 100 100"
                                >
                                    <defs>
                                        <filter id="incomeShadow" x="-20%" y="-20%" width="140%" height="140%">
                                            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor={COLORS.income} floodOpacity="0.4" />
                                        </filter>
                                        <filter id="netLineShadow" x="-20%" y="-20%" width="140%" height="140%">
                                            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor={COLORS.net} floodOpacity="0.3" />
                                        </filter>
                                    </defs>
                                    {/* Despesa (creme, fundo) */}
                                    <path
                                        d={expenseSmoothPath}
                                        fill="none"
                                        stroke={COLORS.prevLine}
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    {/* Receita (laranja, destaque) */}
                                    <path
                                        d={incomeSmoothPath}
                                        fill="none"
                                        stroke={COLORS.income}
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        filter="url(#incomeShadow)"
                                    />
                                    {/* Líquido (verde, tracejado sutil) */}
                                    <path
                                        d={netSmoothPath}
                                        fill="none"
                                        stroke={COLORS.net}
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        strokeDasharray="6 3"
                                        filter="url(#netLineShadow)"
                                    />
                                </svg>
                                
                                {/* Eixo X interativo */}
                                {chartData.map((item, idx) => {
                                    const isHovered = hoveredIdx === idx;
                                    const incomeY = incomePoints[idx] ? 100 - incomePoints[idx].y : 0;
                                    const expenseY = expensePoints[idx] ? 100 - expensePoints[idx].y : 0;
                                
                                    return (
                                        <div 
                                            key={idx} 
                                            className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                            onMouseEnter={() => setHoveredIdx(idx)}
                                            onMouseLeave={() => setHoveredIdx(null)}
                                            style={{ cursor: "crosshair", zIndex: 5 }}
                                        >
                                            {/* Linha Vertical Tracejada */}
                                            {isHovered && (
                                                <div 
                                                    className="position-absolute"
                                                    style={{ 
                                                        height: "calc(100% - 35px)", 
                                                        width: "1.5px", 
                                                        borderLeft: "1.5px dashed rgba(72, 39, 18, 0.2)", 
                                                        bottom: "35px",
                                                        left: "50%",
                                                        transform: "translateX(-50%)",
                                                        pointerEvents: "none",
                                                        zIndex: 0 
                                                    }}
                                                />
                                            )}

                                            {/* Pontos de Destaque */}
                                            {isHovered && incomePoints[idx] && (
                                                <div
                                                    className="position-absolute shadow"
                                                    style={{
                                                        bottom: `calc(${incomeY}% * (1 - 35px / 100%) + 35px)`,
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        left: "50%",
                                                        backgroundColor: "#fff",
                                                        border: `3.5px solid ${COLORS.income}`,
                                                        transform: "translate(-50%, 50%)",
                                                        zIndex: 20,
                                                        pointerEvents: "none"
                                                    }}
                                                />
                                            )}
                                            {isHovered && expensePoints[idx] && (
                                                <div
                                                    className="position-absolute shadow"
                                                    style={{
                                                        bottom: `calc(${expenseY}% * (1 - 35px / 100%) + 35px)`,
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        left: "50%",
                                                        backgroundColor: "#fff",
                                                        border: `3.5px solid ${COLORS.prevLine}`,
                                                        transform: "translate(-50%, 50%)",
                                                        zIndex: 20,
                                                        pointerEvents: "none"
                                                    }}
                                                />
                                            )}

                                            {/* Tooltip */}
                                            {isHovered && (
                                                <div 
                                                    className="position-absolute bg-white shadow-lg rounded-3 p-3 text-start"
                                                    style={{ 
                                                        top: "-48px", 
                                                        width: "200px",
                                                        left: "50%", 
                                                        transform: "translateX(-50%)",
                                                        pointerEvents: "none",
                                                        zIndex: 9999,
                                                        border: "2px solid #482712"
                                                    }}
                                                >
                                                    <h6 className="fw-bold mb-3 pb-2 border-bottom text-center text-uppercase font-size-12" style={{ color: "#482712" }}>{item.display}</h6>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted small d-flex align-items-center">
                                                            <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: COLORS.income }} />
                                                            Entradas
                                                        </span>
                                                        <span className="fw-bold font-size-13" style={{ color: COLORS.income }}>{formatFullCurrency(item.paidIncome || 0)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted small d-flex align-items-center">
                                                            <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: COLORS.prevLine, border: "1px solid rgba(72, 39, 18, 0.15)" }} />
                                                            Pagamentos
                                                        </span>
                                                        <span className="fw-bold font-size-13 text-secondary">{formatFullCurrency(item.paidExpense || 0)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center pt-2 mt-1 border-top">
                                                        <span className="text-muted small d-flex align-items-center">
                                                            <span className="me-2" style={{ width: "10px", height: "3px", borderRadius: "2px", backgroundColor: COLORS.net, display: "inline-block" }} />
                                                            Líquido
                                                        </span>
                                                        <span className="fw-bold font-size-13" style={{ color: (item.paidNet || 0) >= 0 ? COLORS.net : "#ef4444" }}>
                                                            {formatFullCurrency(item.paidNet || 0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Mês no eixo X */}
                                            <span className="position-absolute fw-bold transition-all" style={{ bottom: "-25px", fontSize: "10px", color: isHovered ? "#e2661c" : "#482712" }}>{item.display}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default RevenueExpenseChart;
